import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Typography,
} from "@mui/material";
import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import * as Yup from "yup";
import {
  useGetPasswordSetupInfoQuery,
  useSetupPasswordMutation,
  PasswordSetupInfo,
} from "../../api/authApi";
import BaseFormPage from "../../components/common/BaseForm/BaseFormPage";
import { GenericTextField } from "../../components/common/FormFields";
import { useFormSubmission } from "../../hooks/useFormSubmission";

const validationSchema = Yup.object({
  password: Yup.string()
    .min(6, "Password must be at least 6 characters")
    .max(100, "Password cannot exceed 100 characters")
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^a-zA-Z\d])/,
      "Password must contain at least one lowercase letter, one uppercase letter, one digit, and one special character"
    )
    .required("Password is required"),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("password")], "Passwords must match")
    .required("Confirm password is required"),
});

interface FormValues {
  password: string;
  confirmPassword: string;
}

const PasswordSetupPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const {
    data: setupInfo,
    isLoading: isLoadingInfo,
    error: infoError,
  } = useGetPasswordSetupInfoQuery(token || "", {
    skip: !token,
  });

  
  // Extract the actual data from ApiResponse wrapper
  const actualSetupInfo = setupInfo?.data || setupInfo;
  
  // Type guard to check if we have the wrapped response
  const isWrappedResponse = (response: any): response is { data: PasswordSetupInfo } => {
    return response && typeof response === 'object' && 'data' in response;
  };
  
  const setupInfoData = isWrappedResponse(setupInfo) ? setupInfo.data : setupInfo;

  const [setupPassword, { isLoading: isSettingUp }] = useSetupPasswordMutation();

  const {
    isSubmitting,
    submitError,
    successMessage,
    handleSubmit,
    clearMessages,
  } = useFormSubmission({
    onSuccess: () => {
      setTimeout(() => {
        navigate("/login", {
          state: { message: "Password set up successfully! You can now log in." },
        });
      }, 2000);
    },
    successMessage: "Password set up successfully! Redirecting to login...",
  });

  const formik = useFormik<FormValues>({
    initialValues: {
      password: "",
      confirmPassword: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      if (!token) return;

      await handleSubmit(async () => {
        await setupPassword({
          token,
          password: values.password,
          confirmPassword: values.confirmPassword,
        }).unwrap();
      });
    },
  });

  useEffect(() => {
    clearMessages();
  }, [clearMessages]);

  // Handle loading state
  if (isLoadingInfo) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  // Handle missing token
  if (!token) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Alert severity="error" sx={{ mb: 3 }}>
            Invalid setup link. Please check your email and try again.
          </Alert>
          <Button
            variant="contained"
            onClick={() => navigate("/login")}
            sx={{ mt: 2 }}
          >
            Go to Login
          </Button>
        </Box>
      </Container>
    );
  }

  // Handle invalid token
  if (infoError || !setupInfoData?.isValid) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Alert severity="error" sx={{ mb: 3 }}>
            {setupInfoData?.errorMessage || "Invalid or expired setup link. Please request a new one."}
          </Alert>
          <Button
            variant="contained"
            onClick={() => navigate("/login")}
            sx={{ mt: 2 }}
          >
            Go to Login
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <BaseFormPage
      title="Set Up Your Password"
      subtitle={`Welcome, ${setupInfoData?.firstName} ${setupInfoData?.lastName}! Please create a secure password for your account.`}
      onBack={() => navigate("/login")}
      formik={formik}
      isLoading={isLoadingInfo}
      isSubmitting={isSubmitting || isSettingUp}
      successMessage={successMessage}
      errorMessage={submitError}
      submitButtonText="Save Changes"
      showKeyboardShortcuts={false}
      showUnsavedChangesWarning={false}
      submitButtonSx={{ minWidth: "160px" }}
      maxWidth="sm"
    >
      <Box sx={{ position: "relative" }}>
        <GenericTextField
          name="password"
          label="Password"
          type={showPassword ? "text" : "password"}
          required
          size="medium"
        />
        <Button
          size="small"
          onClick={() => setShowPassword(!showPassword)}
          sx={{ 
            position: "absolute", 
            right: 8, 
            top: "50%", 
            transform: "translateY(-50%)",
            minWidth: "auto", 
            px: 1 
          }}
        >
          {showPassword ? "Hide" : "Show"}
        </Button>
      </Box>

      {/* Password Requirements */}
      <Box sx={{ mt: 1, mb: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Password must contain:
        </Typography>
        <Box component="ul" sx={{ pl: 2, m: 0, "& li": { fontSize: "0.875rem", color: "text.secondary" } }}>
          <li>At least 6 characters</li>
          <li>At least one lowercase letter (a-z)</li>
          <li>At least one uppercase letter (A-Z)</li>
          <li>At least one digit (0-9)</li>
          <li>At least one special character (!@#$%^&* etc.)</li>
        </Box>
      </Box>

      <Box sx={{ position: "relative" }}>
        <GenericTextField
          name="confirmPassword"
          label="Confirm Password"
          type={showConfirmPassword ? "text" : "password"}
          required
          size="medium"
        />
        <Button
          size="small"
          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
          sx={{ 
            position: "absolute", 
            right: 8, 
            top: "50%", 
            transform: "translateY(-50%)",
            minWidth: "auto", 
            px: 1 
          }}
        >
          {showConfirmPassword ? "Hide" : "Show"}
        </Button>
      </Box>

      <Box mt={3} textAlign="center">
        <Typography variant="body2" color="text.secondary">
          Already have an account?{" "}
          <Button
            variant="text"
            onClick={() => navigate("/login")}
            sx={{ textTransform: "none" }}
          >
            Sign in here
          </Button>
        </Typography>
      </Box>
    </BaseFormPage>
  );
};

export default PasswordSetupPage;
